import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useMemo } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { EmptyResourceState } from "@/components/custom/resources/empty-resource-state";
import {
  ResourceContainer,
  type ResourceSection,
} from "@/components/custom/resources/resource-container";
import { ResourceViewSwitcher } from "@/components/custom/resources/resource-view-switcher";
import { useGetServers } from "../api/hooks";
import { ServerSheet } from "../components/server-sheet";
import { filterServers, groupServersByBinding } from "../utils";
import { ServersFilters } from "./components/servers-filters";
import { useServersViewOptions } from "./components/servers-view-options";
import { ServersTable } from "./components/table/servers-table";
import { useServersFilterOptions } from "./components/use-servers-filter-options";

export const ServersList = () => {
  const {
    name: searchTerm = "",
    serverTypes = [],
    statuses = [],
  } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/servers/",
  });
  const navigate = useNavigate();

  const { data: allServers = [] } = useGetServers();

  const {
    columnVisibility,
    setColumnVisibility,
    component: ViewOptionsComponent,
  } = useServersViewOptions();

  // Create filter options
  const { typeOptions, statusOptions } = useServersFilterOptions(allServers);

  // Filter and group servers
  const serversByBinding = useMemo(() => {
    if (!allServers.length) {
      return { project: [], tenant: [], static: [] };
    }

    const filteredServers = filterServers(allServers, {
      nameSearch: searchTerm,
      serverTypes,
      statuses,
    });
    return groupServersByBinding(filteredServers);
  }, [allServers, searchTerm, serverTypes, statuses]);

  const {
    project: projectServers,
    tenant: tenantServers,
    static: staticServers,
  } = serversByBinding;

  // Create sections for display
  const sections: ResourceSection[] = useMemo(() => {
    const allSections: ResourceSection[] = [];
    let isFirstSectionWithData = true;

    if (projectServers.length > 0) {
      allSections.push({
        binding: "project",
        itemsCount: projectServers.length,
        resourceType: "servers",
        defaultOpen: isFirstSectionWithData,
        children: (
          <ServersTable
            columnVisibility={columnVisibility}
            items={projectServers}
            onColumnVisibilityChange={setColumnVisibility}
            stickyColumnBg="background"
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    if (tenantServers.length > 0) {
      allSections.push({
        binding: "tenant",
        itemsCount: tenantServers.length,
        resourceType: "servers",
        defaultOpen: isFirstSectionWithData,
        children: (
          <ServersTable
            columnVisibility={columnVisibility}
            items={tenantServers}
            onColumnVisibilityChange={setColumnVisibility}
            stickyColumnBg="background"
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    if (staticServers.length > 0) {
      allSections.push({
        binding: "static",
        itemsCount: staticServers.length,
        resourceType: "servers",
        defaultOpen: isFirstSectionWithData,
        children: (
          <ServersTable
            columnVisibility={columnVisibility}
            items={staticServers}
            onColumnVisibilityChange={setColumnVisibility}
            stickyColumnBg="background"
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    return allSections;
  }, [
    projectServers,
    tenantServers,
    staticServers,
    columnVisibility,
    setColumnVisibility,
  ]);

  return (
    <ListLayout
      actions={
        <CanProject permission={PROJECT.servers.create}>
          <ServerSheet
            trigger={
              <Button size="sm">
                <Plus className="mr-0 size-4 sm:mr-2" />
                <span className="hidden sm:block">Add Server</span>
              </Button>
            }
          />
        </CanProject>
      }
      description="Add MCP or Arcade Native servers to connect your tools and make them available to your projects."
      filters={
        <>
          <div className="flex items-center gap-2">
            <div className="relative max-w-md">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-full pl-9"
                onChange={(event) => {
                  navigate({
                    to: ".",
                    search: (prev: Record<string, unknown>) => ({
                      ...prev,
                      name: event.target.value,
                    }),
                  });
                }}
                placeholder="Search servers..."
                value={searchTerm}
              />
            </div>
            {ViewOptionsComponent}

            <ServersFilters
              searchTerm={searchTerm}
              serverTypes={serverTypes}
              statuses={statuses}
              statusOptions={statusOptions}
              typeOptions={typeOptions}
            />
          </div>

          <ResourceViewSwitcher />
        </>
      }
      title="Servers"
    >
      {sections.length > 0 ? (
        <ResourceContainer
          defaultActiveTab={`${sections[0].binding}-${sections[0].resourceType}`}
          sections={sections}
        />
      ) : (
        <EmptyResourceState
          description={
            allServers.length > 0
              ? "No servers match your search criteria. Try adjusting your search terms."
              : "Servers allow you to configure and manage HTTP and MCP connections for your applications across different environments."
          }
          resourceType={allServers.length > 0 ? "matching servers" : "servers"}
        />
      )}
    </ListLayout>
  );
};
